import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  TextField,
  TablePagination
} from '@mui/material';
import { Visibility as ViewIcon, Check as ApproveIcon, Close as RejectIcon } from '@mui/icons-material';
import { fetchYojanaApprovalsPage, respondYojanaApproval, describeChangesForDisplay } from '../utils/api';
import { formatLocalDateTime } from '../utils/dateTime';

const YojanaApprovals = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [respondingId, setRespondingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const loadApprovals = useCallback(async (pageNo = page, pageSize = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchYojanaApprovalsPage({ pageNo, pageSize });
      const payload = res?.data;
      const content = Array.isArray(payload?.content) ? payload.content : [];
      setList(content);
      setTotalCount(
        payload?.page?.totalElements ?? payload?.totalElements ?? content.length
      );
    } catch (err) {
      console.error('Error fetching yojana approvals:', err);
      setError(err.response?.data?.message || 'Failed to load approvals.');
      setList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchYojanaApprovalsPage({ pageNo: page, pageSize: rowsPerPage });
        if (cancelled) return;
        const payload = res?.data;
        const content = Array.isArray(payload?.content) ? payload.content : [];
        setList(content);
        setTotalCount(
          payload?.page?.totalElements ?? payload?.totalElements ?? content.length
        );
      } catch (err) {
        console.error('Error fetching yojana approvals:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load approvals.');
          setList([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (item) => {
    setViewItem(item);
    setViewDialogOpen(true);
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setViewItem(null);
  };

  const handleRejectClick = (item) => {
    setRejectItem(item);
    setRejectMessage('');
    setRejectDialogOpen(true);
  };

  const handleRejectDialogClose = () => {
    setRejectDialogOpen(false);
    setRejectItem(null);
    setRejectMessage('');
  };

  const handleRespond = async (item, status, rejectMessageValue) => {
    if (item.status !== 'PENDING') return;
    setRespondingId(item.id);
    try {
      const body = {
        id: item.id,
        yojanaId: item.yojanaId,
        updatedDataJson: item.updatedDataJson,
        requestedByName: item.requestedByName,
        requestedById: item.requestedById,
        requestedDate: item.requestedDate,
        action: item.action,
        status,
        originalDataJson: item.originalDataJson
      };
      if (status === 'REJECTED' && rejectMessageValue != null && String(rejectMessageValue).trim() !== '') {
        body.rejectMessage = String(rejectMessageValue).trim();
      }
      await respondYojanaApproval(body);
      setSnackbar({ open: true, message: `Request ${status.toLowerCase()} successfully.`, severity: 'success' });
      handleRejectDialogClose();
      loadApprovals();
    } catch (err) {
      console.error('Error responding to approval:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || `Failed to ${status.toLowerCase()} request.`,
        severity: 'error'
      });
    } finally {
      setRespondingId(null);
    }
  };

  let parsedData = null;
  if (viewItem?.updatedDataJson) {
    try {
      parsedData = typeof viewItem.updatedDataJson === 'string'
        ? JSON.parse(viewItem.updatedDataJson)
        : viewItem.updatedDataJson;
    } catch {
      parsedData = null;
    }
  }

  let parsedOriginalData = null;
  if (viewItem?.originalDataJson) {
    try {
      parsedOriginalData = typeof viewItem.originalDataJson === 'string'
        ? JSON.parse(viewItem.originalDataJson)
        : viewItem.originalDataJson;
    } catch {
      parsedOriginalData = null;
    }
  }

  const changeList = viewItem?.action === 'UPDATE' && parsedOriginalData && parsedData
    ? describeChangesForDisplay(parsedOriginalData, parsedData)
    : [];

  const getAuditMessage = () => {
    if (!viewItem) return null;
    const fromPayload = parsedData?.auditData?.message ?? null;
    if (fromPayload) return fromPayload;
    const top = viewItem.auditData;
    if (top && typeof top === 'object' && top.message) return top.message;
    if (typeof top === 'string') {
      try {
        const parsed = JSON.parse(top);
        return parsed?.message ?? null;
      } catch {
        return null;
      }
    }
    const raw = viewItem.auditDataJson;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed?.message ?? null;
      } catch {
        return null;
      }
    }
    return viewItem.requestMessage ?? viewItem.auditMessage ?? viewItem.message ?? null;
  };
  const auditMessage = getAuditMessage();

  const hasChangeInfo = auditMessage || changeList.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Yojana Approvals
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Review and approve or reject yojana create/update/delete requests.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Yojana Id</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No approval requests found.
                  </TableCell>
                </TableRow>
              ) : (
                list.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.yojanaId}</TableCell>
                    <TableCell>{row.requestedByName ?? '—'}</TableCell>
                    <TableCell>{formatLocalDateTime(row.requestedDate)}</TableCell>
                    <TableCell>{row.action ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status ?? 'PENDING'}
                        color={
                          row.status === 'APPROVED'
                            ? 'success'
                            : row.status === 'REJECTED'
                              ? 'error'
                              : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleView(row)}
                        title="View"
                      >
                        <ViewIcon />
                      </IconButton>
                      {row.status === 'PENDING' && (
                        <>
                          <Button
                            size="small"
                            color="success"
                            startIcon={respondingId === row.id ? <CircularProgress size={16} /> : <ApproveIcon />}
                            disabled={respondingId !== null}
                            onClick={() => handleRespond(row, 'APPROVED')}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={respondingId === row.id ? null : <RejectIcon />}
                            disabled={respondingId !== null}
                            onClick={() => handleRejectClick(row)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle>
          Request details
          <IconButton
            aria-label="close"
            onClick={handleCloseView}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <RejectIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Requested by: {viewItem.requestedByName} • Action: {viewItem.action} • Yojana ID: {viewItem.yojanaId}
              </Typography>
            </Box>
          )}
          {hasChangeInfo && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                What changed
              </Typography>
              {auditMessage && (
                <Typography variant="body2" sx={{ mb: changeList.length > 0 ? 1 : 0 }}>
                  {auditMessage}
                </Typography>
              )}
              {changeList.length > 0 && (
                <Typography component="ul" variant="body2" sx={{ m: 0, pl: 2 }}>
                  {changeList.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </Typography>
              )}
            </Box>
          )}
          {parsedData && (
            <Box>
              <Typography variant="subtitle2">Title</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{parsedData.title ?? '—'}</Typography>
              <Typography variant="subtitle2">Description</Typography>
              <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                {parsedData.description ?? '—'}
              </Typography>
              {parsedData.eligibility && (
                <>
                  <Typography variant="subtitle2">Eligibility</Typography>
                  <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{parsedData.eligibility}</Typography>
                </>
              )}
              {parsedData.category?.categoryName && (
                <Typography variant="body2" color="text.secondary">Category: {parsedData.category.categoryName}</Typography>
              )}
            </Box>
          )}
          {viewItem && !parsedData && (
            <Typography color="text.secondary">No data to display.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={handleRejectDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Rejection reason</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this request. It will be shown to the requester.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Rejection reason"
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            placeholder="Enter reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectDialogClose}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectMessage.trim() || respondingId !== null}
            onClick={() => rejectItem && handleRespond(rejectItem, 'REJECTED', rejectMessage)}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default YojanaApprovals;
